import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Smartphone, Share, PlusSquare, MoreVertical, ArrowLeft, CheckCircle2, Download } from 'lucide-react';

export default function InstallApp() {
  const [prompt, setPrompt] = useState(null);
  const [installed, setInstalled] = useState(
    window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone
  );
  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', () => setInstalled(true));
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!prompt) return;
    prompt.prompt();
    const { outcome } = await prompt.userChoice;
    if (outcome === 'accepted') setInstalled(true);
    setPrompt(null);
  };

  return (
    <div className="min-h-screen bg-[#0f2b4a] text-white flex flex-col items-center px-5 py-10" data-testid="install-page">
      <Link to="/" className="self-start text-white/60 text-sm flex items-center gap-1 mb-8 hover:text-white">
        <ArrowLeft className="w-4 h-4" /> Back to 2k2day
      </Link>

      <img src="/icons/icon-192.png" alt="2k2day" className="w-24 h-24 rounded-3xl shadow-2xl mb-5" />
      <h1 className="text-2xl sm:text-3xl font-bold">Get the 2k2day App</h1>
      <p className="text-white/60 text-sm mt-2 text-center max-w-sm">
        Install 2k2day on your phone — apply for loans, track your application, chat with us and get notifications. No app store needed.
      </p>

      {installed ? (
        <div className="mt-8 flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/40 rounded-full px-5 py-2.5 text-emerald-300 text-sm font-medium" data-testid="already-installed">
          <CheckCircle2 className="w-4 h-4" /> App installed — open it from your home screen
        </div>
      ) : prompt ? (
        <Button onClick={install} className="mt-8 h-12 px-8 bg-emerald-500 hover:bg-emerald-400 text-[#0f2b4a] font-bold rounded-full text-base" data-testid="install-now-btn">
          <Download className="w-5 h-5 mr-2" /> Install Now
        </Button>
      ) : null}

      <div className="mt-10 w-full max-w-md space-y-4">
        {(isIOS || !prompt) && !installed && (
          <>
            {(!isIOS) && (
              <Card className="bg-white/5 border-white/10 text-white" data-testid="android-instructions">
                <CardContent className="pt-5">
                  <p className="font-semibold flex items-center gap-2 mb-3"><Smartphone className="w-5 h-5 text-emerald-400" /> Android (Chrome)</p>
                  <ol className="space-y-2.5 text-sm text-white/80">
                    <li className="flex items-start gap-2"><MoreVertical className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /> 1. Tap the <b>⋮ menu</b> (top right of Chrome)</li>
                    <li className="flex items-start gap-2"><PlusSquare className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /> 2. Tap <b>Add to Home screen</b> / <b>Install app</b></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-400 shrink-0" /> 3. Tap <b>Install</b> — 2k2day appears on your home screen</li>
                  </ol>
                </CardContent>
              </Card>
            )}
            <Card className="bg-white/5 border-white/10 text-white" data-testid="ios-instructions">
              <CardContent className="pt-5">
                <p className="font-semibold flex items-center gap-2 mb-3"><Smartphone className="w-5 h-5 text-blue-400" /> iPhone (Safari)</p>
                <ol className="space-y-2.5 text-sm text-white/80">
                  <li className="flex items-start gap-2"><Share className="w-4 h-4 mt-0.5 text-blue-400 shrink-0" /> 1. Tap the <b>Share</b> button (bottom of Safari)</li>
                  <li className="flex items-start gap-2"><PlusSquare className="w-4 h-4 mt-0.5 text-blue-400 shrink-0" /> 2. Scroll and tap <b>Add to Home Screen</b></li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-blue-400 shrink-0" /> 3. Tap <b>Add</b> — 2k2day opens like an app</li>
                </ol>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      <p className="text-white/40 text-xs mt-10">Share this page: <span className="font-mono text-white/60">{window.location.origin}/install</span></p>
    </div>
  );
}
